import React from "react";
import { motion } from "framer-motion";
import CTA from "./Components/CTA";

const UxResearch = () => {
    const pillars = [
        {
            title: "Adversarial & Safety Audits",
            description: "We stress-test your prompt structures and data flows to prevent model jailbreaking, prompt injections, data leakages, and operational compliance infractions.",
            icon: (
                <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
            )
        },
        {
            title: "Performance & Cost Benchmarks",
            description: "We benchmark models (accuracy vs size vs cost) to find the perfect tradeoff between latency, SLA requirements, context window limits, and token expenses.",
            icon: (
                <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            )
        },
        {
            title: "On-Device Local Inference",
            description: "Led by a Google Developer Expert for Android, we deploy local neural-inference configurations (NPU/CPU) on edge hardware to secure absolute offline data privacy.",
            icon: (
                <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            )
        }
    ];

    return (
        <div className="select-none min-h-screen bg-[#fafafa] dark:bg-[#191a1a] transition-colors duration-300">
            {/* Hero Section */}
            <section className="max-w-4xl mx-auto px-6 py-16 lg:py-24 text-center space-y-6">
                <motion.div 
                    initial={{ opacity: 0, y: -10 }} 
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <span className="text-xs font-semibold text-forest uppercase tracking-widest bg-forest/10 px-3 py-1.5 rounded-full">AI Evaluation</span>
                </motion.div>
                <motion.h1 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="text-4xl lg:text-5xl font-extrabold text-zinc-900 dark:text-white tracking-tight leading-tight"
                >
                    AI Model Verification & Safety
                </motion.h1>
                <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.25 }}
                    className="text-base lg:text-lg text-zinc-500 dark:text-zinc-400 font-light max-w-2xl mx-auto leading-relaxed"
                >
                    We provide validation protocols to audit LLM behaviors, verify security boundaries, and measure production token efficiency.
                </motion.p>
            </section>

            {/* Content Cards Section */}
            <section className="max-w-5xl mx-auto px-6 pb-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {pillars.map((p, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
                            className="bg-white dark:bg-zinc-900/60 p-8 rounded-3xl border border-zinc-200 dark:border-zinc-800 hover:border-forest/20 shadow-[0_4px_20px_rgba(0,0,0,0.01)] transition-all duration-300"
                        >
                            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6">
                                {p.icon}
                            </div>
                            <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-3">{p.title}</h3>
                            <p className="text-sm font-light text-zinc-500 dark:text-zinc-400 leading-relaxed">
                                {p.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CTA section */}
            <CTA />
        </div>
    );
};

export default UxResearch;